import { DealDeskDiscount, Discount, Product } from '../types/types'

// Import shared functions from common module
import {
   //@ts-ignore
   generateUUID as generateUUIDBase,
   //@ts-ignore
   isUUID as isUUIDBase,
   //@ts-ignore
   convertDealDeskDiscountToStandardFormat as convertBase,
} from '../common/dealDeskDiscountConverter.mjs'

// Re-export shared functions for external use
export const generateUUID = generateUUIDBase as () => string
export const isUUID = isUUIDBase as (str: string) => boolean

/**
 * TypeScript wrapper for convertDealDeskDiscountToStandardFormat
 * Provides type safety for client-side usage
 */
export function convertDealDeskDiscountToStandardFormat(
   dealDeskDiscount: DealDeskDiscount,
   product_list: Record<string, Product> = {},
): Discount {
   return convertBase(dealDeskDiscount, product_list) as Discount
}

/**
 * Calculates which products should be locked based on deal desk discounts
 * Products are locked if ANY deal desk discount applies to them OR if old deal desk system has active discounts
 * @param dealDeskDiscounts - Array of deal desk discounts from new modal
 * @param discount_list - Current discount list to check for old deal desk discounts
 * @param oldDealDeskRates - Object with old deal desk discount rates
 * @returns Set of product codes that should be locked
 */
export function calculateDealDeskLocks(
   dealDeskDiscounts: DealDeskDiscount[],
   discount_list?: Record<string, Discount>,
   oldDealDeskRates?: {
      hr_dealdesk_imp_discount_rate?: number
      quickstart_dealdesk_imp_discount_rate?: number
      payroll_dealdesk_imp_discount_rate?: number
   },
): Set<string> {
   const locks = new Set<string>()

   // Add locks from new deal desk discounts
   dealDeskDiscounts.forEach((discount) => {
      if (discount.apply_to && Array.isArray(discount.apply_to)) {
         discount.apply_to.forEach((productCode) => {
            locks.add(productCode)
         })
      }
   })

   // Add locks from old deal desk system if rates are active
   if (discount_list && oldDealDeskRates) {
      // Helper function to get ApplyTo products from a discount by key
      const getApplyToForDiscount = (discountKey: string): string[] => {
         const discount = discount_list[discountKey]
         if (!discount?.ApplyTo) return []
         return discount.ApplyTo.split(',').map((item: string) => item.trim())
      }

      // Check HR Implementation discount (hrDealdesk)
      if (
         oldDealDeskRates.hr_dealdesk_imp_discount_rate &&
         oldDealDeskRates.hr_dealdesk_imp_discount_rate > 0
      ) {
         const products = getApplyToForDiscount('hrDealdesk')
         products.forEach((productCode) => locks.add(productCode))
      }

      // Check QuickStart discount (quickstartDiscount)
      if (
         oldDealDeskRates.quickstart_dealdesk_imp_discount_rate &&
         oldDealDeskRates.quickstart_dealdesk_imp_discount_rate > 0
      ) {
         const products = getApplyToForDiscount('quickstartDiscount')
         products.forEach((productCode) => locks.add(productCode))
      }

      // Check Payroll Implementation discount (payDealdesk)
      if (
         oldDealDeskRates.payroll_dealdesk_imp_discount_rate &&
         oldDealDeskRates.payroll_dealdesk_imp_discount_rate > 0
      ) {
         const products = getApplyToForDiscount('payDealdesk')
         products.forEach((productCode) => locks.add(productCode))
      }
   }

   return locks
}

/**
 * Removes deal desk discounts from discount_list by their IDs
 * @param discountList - Current discount list
 * @param dealDeskDiscountIds - Array of deal desk discount IDs to remove
 * @returns New discount list with deal desk discounts removed
 */
export function removeDealDeskDiscountsFromList(
   discountList: Record<string, Discount>,
   dealDeskDiscountIds: string[],
): Record<string, Discount> {
   const cleaned = { ...discountList }

   dealDeskDiscountIds.forEach((id) => {
      delete cleaned[id]
   })

   return cleaned
}

/**
 * Removes all deal desk discounts from discount_list
 * Identifies them by UUID format or temp- prefix in the discount code
 * @param discountList - Current discount list
 * @returns New discount list with all deal desk discounts removed
 */
export function removeAllDealDeskDiscountsFromList(
   discountList: Record<string, Discount>,
): Record<string, Discount> {
   const cleaned = { ...discountList }

   Object.keys(cleaned).forEach((key) => {
      if (isUUID(key) || key.startsWith('temp-')) {
         delete cleaned[key]
      }
   })

   return cleaned
}
